import { useQuery } from "@apollo/client";
import IconArrowBack from "@mui/icons-material/ArrowBack";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import AddressAutocompleteField from "../AddressAutocompleteField";
import { AddressGetLocationDocument } from "./queries.generated";
import { MapCenterAddress } from "./types";

type MapCenterAddressPanelProps = {
    address: MapCenterAddress | null;
    onBack: () => void;
    onCenter: (address: MapCenterAddress) => void;
};

export function MapCenterAddressPanel({ address, onBack, onCenter }: MapCenterAddressPanelProps) {
    const { t } = useTranslation("map");

    const [selectedAddressId, setSelectedAddressId] = useState<string>();
    const { data, error } = useQuery(AddressGetLocationDocument, {
        variables: { addressId: selectedAddressId ?? "" },
        skip: !selectedAddressId,
    });
    const selectedAddress = selectedAddressId === undefined ? address : data?.addressGetLocation;

    const handleCenter = () => {
        if (!selectedAddress) return;

        onCenter(selectedAddress);
    };

    return (
        <Stack>
            <Stack
                direction="row"
                alignItems="center"
                columnGap={0.5}
                paddingInline={1}
                paddingBlock={0.5}
                sx={theme => ({ borderBottom: `1px solid ${theme.palette.divider}` })}
            >
                <IconButton size="small" aria-label={t("mapButtonFitBounds.back")} onClick={onBack}>
                    <IconArrowBack fontSize="small" />
                </IconButton>
                <Typography variant="body2" fontWeight="bold">
                    {t("mapButtonFitBounds.option.address")}
                </Typography>
            </Stack>

            <Stack padding={2} rowGap={1.5}>
                <AddressAutocompleteField
                    size="small"
                    fieldLabel={t("mapButtonFitBounds.labelAddress")}
                    noOptionsText={t("mapButtonFitBounds.labelAddressNoOptions")}
                    initialValue={address?.fullAddress ?? null}
                    disabled={false}
                    onSelect={setSelectedAddressId}
                />

                {error && (
                    <Typography variant="caption" color="error">
                        {t("mapButtonFitBounds.addressLookupFailed")}
                    </Typography>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button variant="contained" size="small" disabled={!selectedAddress} onClick={handleCenter}>
                        {t("mapButtonFitBounds.btnCenter")}
                    </Button>
                </Box>
            </Stack>
        </Stack>
    );
}
